import './attributes-dashboard-widget.less'
import {
  ACTION_TOP_ATTRIBUTES_FETCH,
  AttributeItem,
  AttributesTopListByDataSource,
  fetchTopAttributes,
  IGetAttributesFilterParams,
  resetAttributesAll
} from './attributesSlice'
import { getShowLoader } from '../../reducers/requestReducer'
import { RootState } from '../../rootReducer'
import useUrlContext from '../../hooks/useUrlContext'
import {
  DATA_SOURCE_ID,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_TYPES,
  FILTER_ATTRIBUTE_NAME,
  FILTER_SENSITIVITY_MULTIPLE,
  GRAPHQL_API_FILTERS,
  IS_AT_RISK,
  MAP_ATTRIBUTE_OTHER_CLASS_TO_COLORS,
  MAP_ATTRIBUTE_TOP_CLASS_TO_COLORS,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  SENSITIVE_LABEL,
  STRUCTURED_DATASOURCES,
  URL_ATTRIBUTES,
  URL_COLUMNS
} from '../../constants'

import ChartPie, { ChartPieItem } from '../../components/ChartPie/chartPie'
import LoaderView from '../../components/Loader/loader'
import { getUpdatedUrl, managePageFiltersMultiple } from '../../utils/urlUtil'
import NoSensitiveDataSvg from '../../assets/inlineSvg/noData.svg'
import { mockAttributeDashboardWidgetV2Data } from '../dashboards/renderer/utils/widgetMockdata'
import { DataSensitivityLevels } from '../accessControl/types'
import { getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../dashboards/mainDashboardv2/constants'
import { getDataSourceBasePath } from '../../utils/dataSourceUtil'
import { useHistory } from 'react-router-dom'
import React, { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { Typography } from '@lightbeamai/design-system'

const TOP_ATTRIBUTES_COUNT = 4

type ChartPieItemWithAnnotation = ChartPieItem & {
  extraValue?: number
  internalName?: string
}

interface OwnProps {
  parentUrl?: string
  dataSourceId?: string
  legendPosition?: 'bottom' | 'right'
  piClass?: string
  showListLegend?: boolean
  chartSize?: number
  isAnimated?: boolean
  isHighSensitive?: boolean
  title?: string
  help?: string
  showAllAttributes?: boolean
  className?: string
  isClickable?: boolean
  useMockData?: boolean
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
}

interface StateProps {
  topListByDs?: AttributesTopListByDataSource[]
  topHighSensitiveList?: AttributesTopListByDataSource[]
  showLoader: boolean
}

interface DispatchProps {
  fetchTopAttributes: (
    params: IGetAttributesFilterParams & { [UNIQUE_WIDGET_INDEX]: string }
  ) => void
  resetAttributesAll: () => void
}

type AttributesDashboardWidgetV2ComponentProps = StateProps & DispatchProps & OwnProps

const COLOR_DEFAULT = '#A1DAB4'

const AttributesDashboardWidgetV2 = (
  props: AttributesDashboardWidgetV2ComponentProps
): React.ReactElement => {
  const {
    topListByDs = [],
    topHighSensitiveList = [],
    showLoader,
    showListLegend = true,
    parentUrl = '',
    dataSourceId = '',
    legendPosition = 'bottom',
    piClass = 'xs-mb-36 xs-mt-36',
    chartSize = 150,
    isAnimated = true,
    fetchTopAttributes,
    resetAttributesAll,
    isHighSensitive = false,
    title,
    help,
    showAllAttributes = false,
    className,
    isClickable = true,
    useMockData = false,
    widgetDatasourceId,
    widgetDashboardId,
    widgetDatasourceType
  } = props
  const intl = useIntl()
  const context = useUrlContext()
  const history = useHistory()

  const datasourceId =
    widgetDatasourceId || dataSourceId || context.contextFilters[DATA_SOURCE_ID] || ''
  const datasourceType = context?.queryParams[DATA_SOURCE_TYPE] as DATA_SOURCE_TYPES

  const widgetIndex = getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
  const list = useMemo((): AttributeItem[] => {
    let potentialList: any[] = []
    if (useMockData) {
      const mockSourceArray = isHighSensitive
        ? mockAttributeDashboardWidgetV2Data.topHighSensitiveList
        : mockAttributeDashboardWidgetV2Data.topListByDs

      potentialList = mockSourceArray[0].list || []
    } else {
      const realSourceArray = isHighSensitive ? topHighSensitiveList : topListByDs
      const item = realSourceArray?.find((item) => item.datasourceId === datasourceId)

      if (item?.list?.length) {
        potentialList = item.list
      } else if (item?.listWithColumnClassification?.length) {
        potentialList = item.listWithColumnClassification
      } else {
        potentialList = []
      }
    }
    return potentialList
  }, [isHighSensitive, datasourceId, topHighSensitiveList, topListByDs, useMockData])

  useEffect(() => {
    if (useMockData) return
    fetchTopAttributes({
      datasourceIds: [datasourceId],
      filter: [],
      hasInstances: true,
      ...(isHighSensitive ? { sensitivity: SENSITIVE_LABEL.HIGH } : {}),
      ...(isHighSensitive
        ? showAllAttributes
          ? {}
          : { booleanFilter: [{ key: GRAPHQL_API_FILTERS.enabled, value: true }] }
        : {}),
      [UNIQUE_WIDGET_INDEX]: widgetIndex
    })
    return () => resetAttributesAll()
  }, [
    datasourceId,
    fetchTopAttributes,
    isHighSensitive,
    resetAttributesAll,
    showAllAttributes,
    useMockData,
    widgetIndex
  ])
  const pathBase = getDataSourceBasePath(
    widgetDatasourceType,
    datasourceId,
    context.pathname,
    false
  )

  const isStructured = STRUCTURED_DATASOURCES.includes(datasourceType)
  const baseUrl = parentUrl ? parentUrl + URL_ATTRIBUTES : pathBase + URL_ATTRIBUTES
  const baseUrlColumns = parentUrl ? parentUrl + URL_COLUMNS : pathBase + URL_COLUMNS

  const hasColumns = list.some((attribute) => attribute.columnCount)
  const hasInstances = list.some((attribute) => attribute.attributesInstanceCount)

  const attributes = list.slice().sort((a, b) => {
    return hasColumns
      ? (b.columnCount || 0) - (a.columnCount || 0)
      : (b.attributesInstanceCount || 0) - (a.attributesInstanceCount || 0)
  })

  const top: ChartPieItemWithAnnotation[] = attributes
    .slice(0, TOP_ATTRIBUTES_COUNT)
    .map((a, i) => {
      return {
        key: a.name,
        internalName: a.internalName,
        value: (hasColumns ? a.columnCount : a.attributesInstanceCount) || 0,
        color: MAP_ATTRIBUTE_TOP_CLASS_TO_COLORS[i] || COLOR_DEFAULT,
        extraValue: hasColumns ? a.attributesInstanceCount || 0 : 0
      }
    })

  const topKeys = top.map((a) => a.key)
  const others: ChartPieItemWithAnnotation = attributes
    .filter((a) => !topKeys.includes(a.name))
    .reduce(
      (acc, current) => {
        acc.value += (hasColumns ? current.columnCount : current.attributesInstanceCount) || 0
        acc.extraValue += hasColumns ? current.attributesInstanceCount || 0 : 0
        return acc
      },
      {
        key: 'Others',
        value: 0,
        extraValue: 0,
        color: MAP_ATTRIBUTE_OTHER_CLASS_TO_COLORS[0] || COLOR_DEFAULT
      }
    )

  const filteredCategories: ChartPieItemWithAnnotation[] = others.value ? [...top, others] : top

  const handleOpenPageWithFilter = (value: string) => {
    const urlName = list.find(({ name: attributeName }) => attributeName === value)

    if (isStructured && urlName?.id && value !== 'Others') {
      const url = getUpdatedUrl({
        pathName: baseUrlColumns,
        pageName: URL_COLUMNS,
        pageQueryParams: {
          [IS_AT_RISK]: true,
          [FILTER_ATTRIBUTE_NAME]: urlName?.id
        }
      })
      return history.push(url)
    }

    if (value === 'Others') {
      const attributeNames = list
        .filter((attribute) => !topKeys.includes(attribute.name) && attribute.internalName)
        .map((attribute) => attribute.internalName) as string[]

      managePageFiltersMultiple({
        pathname: baseUrl,
        pageName: URL_ATTRIBUTES,
        filters: [
          {
            filterKey: PAGE_VIEW_KEY,
            filterValues: [PAGE_VIEW_VALUES.card],
            action: 'add'
          },
          {
            filterKey: FILTER_ATTRIBUTE_NAME,
            filterValues: attributeNames,
            action: 'add'
          }
        ],
        newQueryParams: {
          ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType }),
          ...(dataSourceId && { [DATA_SOURCE_ID]: dataSourceId })
        }
      })
    } else if (urlName && urlName.internalName) {
      managePageFiltersMultiple({
        pathname: baseUrl,
        pageName: URL_ATTRIBUTES,
        filters: [
          {
            filterKey: PAGE_VIEW_KEY,
            filterValues: [PAGE_VIEW_VALUES.list],
            action: 'add'
          },
          {
            filterKey: FILTER_ATTRIBUTE_NAME,
            filterValues: [urlName.internalName],
            action: 'add'
          }
        ],
        newQueryParams: {
          ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType }),
          ...(dataSourceId && { [DATA_SOURCE_ID]: dataSourceId })
        }
      })
    }
  }

  const totalLink = getUpdatedUrl({
    pathName: baseUrl,
    pageName: URL_ATTRIBUTES,
    pageQueryParams: isHighSensitive
      ? {
          [FILTER_SENSITIVITY_MULTIPLE]: DataSensitivityLevels.High
        }
      : {},
    queryParams: {
      ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType }),
      ...(dataSourceId && { [DATA_SOURCE_ID]: dataSourceId })
    }
  })

  return (
    <div className="lb-widget-card xs-p-16">
      <div className={`${className} attribute-dashboard-widget-v2-container`}>
        <div className="dashboard-container-title xs-mb-0">
          <Typography as="span" variant="h6" color="primary">
            {title ||
              intl.formatMessage({
                id: 'attributes.widget.distribution.title'
              })}
            : &nbsp;
            {isHighSensitive
              ? intl.formatMessage({
                  id: 'attributes.widget.distribution.sensitivity'
                })
              : intl.formatMessage({
                  id: 'attributes.widget.distribution.all'
                })}
            {help}
          </Typography>
        </div>
        {hasColumns && (
          <Typography variant="baseMinus2">
            {intl.formatMessage({
              id: hasInstances
                ? 'attributes.widget.distribution.totalColumnsAndInstances'
                : 'attributes.widget.distribution.totalColumns'
            })}
          </Typography>
        )}
        <div
          data-test-id="attributes-attributes-dashboard-widget"
          className={`attributes widget-v2-grouped ${
            legendPosition === 'bottom' ? 'vertical' : 'horizontal'
          }`}
        >
          {list && filteredCategories.length ? (
            <div className="chart-wrapper">
              <ChartPie
                data={filteredCategories.slice().reverse()}
                onClick={handleOpenPageWithFilter}
                width={chartSize}
                gap={5}
                // showHalfCircle
                centerCountStyles={{ fontSize: '2.1rem', fontWeight: 700 }}
                totalLink={totalLink}
                className={piClass}
                isAnimated={isAnimated}
                isClickable={isClickable}
              />
            </div>
          ) : null}
          <div className={filteredCategories.length ? 'legend' : 'empty-legend'}>
            {filteredCategories.length ? (
              <>
                {legendPosition === 'right' && showListLegend && (
                  <div className="legend-horizontal-title">
                    {intl.formatMessage(
                      { id: 'attributes.widget.distribution.topAttributes' },
                      { count: filteredCategories.length }
                    )}
                  </div>
                )}
                {filteredCategories.map((item, i) => {
                  const goToFilterPage = () => handleOpenPageWithFilter(item.key)
                  const formattedCount = intl.formatNumber(item.value, { notation: 'compact' })
                  const formattedExtraValue = intl.formatNumber(item.extraValue ?? 0, {
                    notation: 'compact'
                  })

                  return (
                    <div className="item btn" key={i} onClick={goToFilterPage}>
                      <div className="item-container">
                        <div className="circle" style={{ backgroundColor: item.color }} />
                        <Typography as="span" variant="baseMinus2" color="primary" className="key">
                          {item.key}
                        </Typography>
                      </div>

                      <Typography
                        as="span"
                        variant="baseMinus2"
                        color="primary"
                        type="strong"
                        className="value"
                      >
                        {legendPosition === 'bottom' ? `(${formattedCount})` : formattedCount}
                        {hasColumns && hasInstances ? ` (${formattedExtraValue})` : ''}
                      </Typography>
                    </div>
                  )
                })}
              </>
            ) : (
              <div className="icon-box empty-state-text">
                <NoSensitiveDataSvg />
                <div>{intl.formatMessage({ id: 'dashboard.widget.noSensitiveData' })}</div>
              </div>
            )}
          </div>
          <LoaderView showLoader={showLoader} size="small" />
        </div>
        {hasColumns && (
          <Typography
            variant="baseMinus4"
            style={{ textAlign: 'end', fontStyle: 'italic' }}
            className="xs-mt-8"
          >
            {intl.formatMessage({
              id: 'attributes.widget.distribution.columnsAndInstancesClue'
            })}
          </Typography>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  const widgetIndex = getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)

  const widgetTopList = state.attributes.attributes?.widgetDataTopList || {}
  const widgetHighSensitiveList = state.attributes.attributes?.widgetDataHighSensitiveTopList || {}

  const topListForWidget = widgetTopList[widgetIndex] || []
  const highSensitiveListForWidget = widgetHighSensitiveList[widgetIndex] || []

  return {
    topListByDs: topListForWidget.filter((item) => !item.isHighSensitive),
    topHighSensitiveList: highSensitiveListForWidget,
    showLoader: getShowLoader(ACTION_TOP_ATTRIBUTES_FETCH)
  }
}

const mapDispatchToProps: DispatchProps = { fetchTopAttributes, resetAttributesAll }

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(AttributesDashboardWidgetV2)
